import * as queryString from 'query-string';

import { globalState } from '@/components/Global/GlobalDataManagement';
import { getFetchOptionsWithAuth } from '@/dataExchange/utils/fetchOptions';
import { ApiResponseWithPagination } from '@/domain/common';
import { CreateRequestParamsDto, RequestResponseDto } from '@/domain/newRequest';
import { AbsenceResponseDto, AllRequestsParamsDto, ChangeHistoryStatusResponseDto } from '@/domain/requestsPage';
import { HTTP_METHOD } from '@/utils/enums/dataExchange';
import { REQUEST_STATUS } from '@/utils/enums/requestsPage';

export const getAllRequests = async (
    params: AllRequestsParamsDto
): Promise<ApiResponseWithPagination<AbsenceResponseDto[]>> => {
    const queryStringParams = queryString.stringify(params, { skipEmptyString: true, skipNull: true });

    const response = await fetch(
        `${globalState.config.FQ_BASE_API_URL}/v2/absence?${queryStringParams}`,
        getFetchOptionsWithAuth({
            method: HTTP_METHOD.GET,
        })
    );

    if (!response.ok) {
        const parsedResponse = await response.json();
        throw parsedResponse;
    }

    return response.json();
};

export const getRequest = async (absenceId: string): Promise<AbsenceResponseDto> => {
    const response = await fetch(
        `${globalState.config.FQ_BASE_API_URL}/absence/${absenceId}`,
        getFetchOptionsWithAuth({
            method: HTTP_METHOD.GET,
        })
    );

    if (!response.ok) {
        const parsedResponse = await response.json();
        throw parsedResponse;
    }

    return response.json();
};

export const getRequestStatusChangesHistory = async (absenceId: string): Promise<ChangeHistoryStatusResponseDto> => {
    const response = await fetch(
        `${globalState.config.FQ_BASE_API_URL}/absence/history/${absenceId}/status-acceptance`,
        getFetchOptionsWithAuth({
            method: HTTP_METHOD.GET,
        })
    );

    if (!response.ok) {
        const parsedResponse = await response.json();
        throw parsedResponse;
    }

    return response.json();
};

export const updateAbsenceStatus = async ({
    absenceId,
    status,
}: {
    absenceId: string;
    status: REQUEST_STATUS;
}): Promise<ChangeHistoryStatusResponseDto> => {
    const response = await fetch(
        `${globalState.config.FQ_BASE_API_URL}/absence/${absenceId}/status`,
        getFetchOptionsWithAuth({
            method: HTTP_METHOD.PUT,
            body: { status },
        })
    );

    if (!response.ok) {
        const parsedResponse = await response.json();
        throw parsedResponse;
    }

    return response.json();
};

export const updateRequestData = async ({
    absenceId,
    userRequestBody,
}: {
    absenceId?: string;
    userRequestBody: CreateRequestParamsDto;
}): Promise<RequestResponseDto> => {
    const response = await fetch(
        `${globalState.config.FQ_BASE_API_URL}/absence/${absenceId}`,
        getFetchOptionsWithAuth({
            method: HTTP_METHOD.PUT,
            body: userRequestBody,
        })
    );

    if (!response.ok) {
        const parsedResponse = await response.json();
        throw parsedResponse;
    }

    return response.json();
};

export const absenceDeletionRequest = async (absenceId: string): Promise<null> => {
    const response = await fetch(
        `${globalState.config.FQ_BASE_API_URL}/absence/${absenceId}`,
        getFetchOptionsWithAuth({
            contentType: null,
            method: HTTP_METHOD.DELETE,
        })
    );

    if (!response.ok) {
        const parsedResponse = await response.json();
        throw parsedResponse;
    }

    return response.json();
};

export const acceptRequestDeletion = async (absenceId: string): Promise<null> => {
    const response = await fetch(
        `${globalState.config.FQ_BASE_API_URL}/absence/delete/${absenceId}/accept`,
        getFetchOptionsWithAuth({
            contentType: null,
            method: HTTP_METHOD.POST,
        })
    );

    if (!response.ok) {
        const parsedResponse = await response.json();
        throw parsedResponse;
    }

    return response.json();
};

export const rejectRequestDeletion = async (absenceId: string): Promise<null> => {
    const response = await fetch(
        `${globalState.config.FQ_BASE_API_URL}/absence/delete/${absenceId}/reject`,
        getFetchOptionsWithAuth({
            contentType: null,
            method: HTTP_METHOD.POST,
        })
    );

    if (!response.ok) {
        const parsedResponse = await response.json();
        throw parsedResponse;
    }

    return response.json();
};
